import { AlertTriangle, ArrowLeft, Download, Loader2, RefreshCw, Send, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { invoicePdfUrl } from '@/data/invoicesRepo';
import { useInvoice } from '@/hooks/useInvoices';
import { useInvoicingSettings } from '@/hooks/useInvoicingSettings';
import {
  formatCommissionDeduction,
  invoiceLineCommissionMark,
  invoiceLineDescription,
  invoiceLineReference,
  invoicePeriodLabel,
  invoiceStatusLabels,
  type InvoiceStatusType,
  type InvoiceType,
} from '@/types/types_invoice';
import {
  callableErrorMessage,
  generateInvoiceDraft,
  issueInvoice,
  regenerateInvoicePdf,
  voidInvoice,
} from '@/util/callable_functions';
import { formatCalendarDate, formatDate, formatJpy } from '@/util/util_format';

/** Which button is mid-flight, so the others can be held while one runs. */
type BusyType = 'generate' | 'issue' | 'void' | 'pdf' | null;

const statusPillClass: Record<InvoiceStatusType, string> = {
  draft: 'tw:bg-amber-100 tw:text-amber-900',
  issued: 'tw:bg-green-100 tw:text-green-800',
  void: 'tw:bg-gray-100 tw:text-gray-600',
};

/**
 * One invoice: what it bills, what it comes to, and the two buttons that decide
 * its life.
 *
 * The page is deliberately arranged around the one irreversible step. A draft is
 * arithmetic — regenerate it as often as you like. Issuing stamps every booking
 * it names, allocates the number and renders the document, after which nothing
 * recomputes it. Everything below is written so that difference is visible
 * before the button is pressed rather than explained afterwards.
 */
export function PageInvoiceDetail() {
  const { invoiceId = '' } = useParams();
  const { invoice, loading } = useInvoice(invoiceId);
  const { missing } = useInvoicingSettings();

  const [busy, setBusy] = useState<BusyType>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingIssue, setConfirmingIssue] = useState(false);
  const [confirmingVoid, setConfirmingVoid] = useState(false);

  async function run(what: Exclude<BusyType, null>, action: () => Promise<unknown>) {
    setBusy(what);
    setError(null);
    try {
      await action();
    } catch (e) {
      console.error(`${what} invoice failed`, e);
      setError(callableErrorMessage(e));
    } finally {
      setBusy(null);
      setConfirmingIssue(false);
      setConfirmingVoid(false);
    }
  }

  async function handleDownload(invoice: InvoiceType) {
    if (!invoice.pdfPath) return;
    try {
      // Resolved on demand with the reader's own credentials rather than stored
      // on the document — see `invoicePdfUrl`.
      window.open(await invoicePdfUrl(invoice.pdfPath), '_blank', 'noopener');
    } catch (e) {
      console.error('Could not resolve the invoice PDF', e);
      setError('Could not open the PDF. It may still be rendering.');
    }
  }

  if (loading) return <p className='tw:text-sm tw:text-ed-stone'>Loading…</p>;

  if (!invoice) {
    return (
      <div>
        <BackLink />
        <p className='tw:mt-6 tw:text-sm tw:text-ed-stone'>That invoice could not be found.</p>
      </div>
    );
  }

  const isDraft = invoice.status === 'draft';

  return (
    <div className='tw:mx-auto tw:max-w-5xl'>
      <BackLink />

      <header className='tw:mt-4 tw:mb-6 tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-4'>
        <div>
          <div className='tw:flex tw:items-center tw:gap-3'>
            <h1 className='tw:font-serif tw:text-2xl tw:text-ed-black'>{invoice.partnerName}</h1>
            <span className={`tw:rounded-full tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium ${statusPillClass[invoice.status]}`}>
              {invoiceStatusLabels[invoice.status]}
            </span>
          </div>
          <p className='tw:mt-1 tw:text-sm tw:text-ed-stone'>
            {invoicePeriodLabel(invoice.period)}
            {invoice.invoiceNumber && <> · {invoice.invoiceNumber}</>}
            {invoice.issuedAt && <> · issued {formatDate(invoice.issuedAt)} by {invoice.issuedByName}</>}
          </p>
        </div>

        <div className='tw:flex tw:flex-wrap tw:gap-2'>
          {isDraft && (
            <>
              <Button
                variant='outline'
                disabled={busy !== null}
                onClick={() => void run('generate', () =>
                  generateInvoiceDraft({ partnerId: invoice.partnerId, period: invoice.period }))}
              >
                {busy === 'generate' ? <Loader2 className='tw:size-4 tw:animate-spin' /> : <RefreshCw className='tw:size-4' />}
                Regenerate
              </Button>
              <Button
                disabled={busy !== null || invoice.lines.length === 0 || missing.length > 0}
                onClick={() => setConfirmingIssue(true)}
              >
                <Send className='tw:size-4' /> Issue invoice
              </Button>
            </>
          )}

          {invoice.status === 'issued' && (
            <>
              <Button variant='outline' disabled={!invoice.pdfPath} onClick={() => void handleDownload(invoice)}>
                <Download className='tw:size-4' /> Download PDF
              </Button>
              {/*
                * Sits beside Void deliberately, because the two are the ones easily
                * confused and only one of them touches the money. This produces the
                * document again from a record that has not changed; Void takes the
                * invoice back and moves its bookings onto next month. A wrong PDF
                * wants the first, and reaching for the second costs a month.
                */}
              <Button
                variant='outline'
                disabled={busy !== null}
                onClick={() => void run('pdf', () => regenerateInvoicePdf({ invoiceId }))}
              >
                {busy === 'pdf' ? <Loader2 className='tw:size-4 tw:animate-spin' /> : <RefreshCw className='tw:size-4' />}
                {/* Nothing to *re*generate when the first render never produced one. */}
                {invoice.pdfPath ? 'Regenerate PDF' : 'Render PDF'}
              </Button>
              <Button variant='outline' disabled={busy !== null} onClick={() => setConfirmingVoid(true)}>
                <Undo2 className='tw:size-4' /> Void
              </Button>
            </>
          )}
        </div>
      </header>

      {missing.length > 0 && isDraft && (
        <Notice>
          Invoicing is not set up yet — {missing.join(', ')} still to fill in under Settings. The draft
          below is complete; it just cannot be issued as a document nobody could pay.
        </Notice>
      )}

      {invoice.pdfError && (
        <Notice>
          This invoice was issued, but its PDF could not be rendered: {invoice.pdfError}. The bookings
          are billed and the figures stand — only the document is missing. Render PDF above produces
          it again; there is nothing to correct and nothing to void.
        </Notice>
      )}

      {invoice.status === 'void' && (
        <Notice>
          Voided {formatDate(invoice.voidedAt)}. Its bookings were released and will appear on the
          next draft.
        </Notice>
      )}

      {error && <Notice>{error}</Notice>}

      <section className='tw:mb-6 tw:grid tw:gap-4 tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:p-5 tw:text-sm tw:shadow-sm tw:sm:grid-cols-3'>
        <Detail label='件名'>{invoice.subject}</Detail>
        <Detail label='支払期限'>{invoice.dueDate ? formatCalendarDate(invoice.dueDate) : 'Set when issued'}</Detail>
        <Detail label='御中'>{invoice.partnerName}</Detail>
      </section>

      <div className='tw:overflow-x-auto tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:shadow-sm'>
        <table className='tw:w-full'>
          <thead className='tw:bg-ed-cream/50'>
            <tr className='tw:text-left tw:text-xs tw:tracking-wider tw:uppercase tw:text-ed-stone'>
              {/* First, as on the PDF this screen is the preview of. It is the
                  column a partner queries a line by, so an admin reading the two
                  side by side should not have to map one onto the other. */}
              <th className='tw:px-4 tw:py-3'>参照番号</th>
              <th className='tw:px-4 tw:py-3'>摘要</th>
              <th className='tw:px-4 tw:py-3 tw:text-right'>税率</th>
              <th className='tw:px-4 tw:py-3 tw:text-right'>金額（税込）</th>
              <th className='tw:px-4 tw:py-3 tw:text-right'>Commission</th>
            </tr>
          </thead>
          <tbody className='tw:divide-y tw:divide-ed-umber/10 tw:text-sm'>
            {invoice.lines.length === 0 && (
              <tr>
                <td colSpan={5} className='tw:px-4 tw:py-8 tw:text-center tw:text-ed-stone'>
                  Nothing to bill this partner for this month.
                </td>
              </tr>
            )}
            {invoice.lines.map((line, i) => (
              <tr key={`${line.requestId}-${line.kind}-${i}`}>
                <td className='tw:px-4 tw:py-3 tw:font-mono tw:text-xs tw:whitespace-nowrap tw:text-ed-stone'>
                  {invoiceLineReference(line)}
                </td>
                <td className='tw:px-4 tw:py-3'>
                  <Link
                    to='/admin/requests'
                    className='tw:underline-offset-4 tw:hover:underline'
                    title='Booking requests'
                  >
                    {/* ＊ where no commission was taken, as the PDF prints it. Not the rate
                        note the PDF also carries — the Commission column beside this states
                        the rate on every line already. */}
                    {invoiceLineCommissionMark(line, invoice.commissionDeductions)}{invoiceLineDescription(line)}
                  </Link>
                </td>
                <td className='tw:px-4 tw:py-3 tw:text-right tw:tabular-nums'>{line.taxRate}%</td>
                <td className='tw:px-4 tw:py-3 tw:text-right tw:tabular-nums'>{formatJpy(line.gross)}</td>
                <td className='tw:px-4 tw:py-3 tw:text-right tw:tabular-nums tw:text-ed-stone'>
                  {line.commissionAmount
                    ? `${formatCommissionDeduction(line.commissionAmount, formatJpy)} (${line.commissionPercent}%)`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className='tw:border-t tw:border-ed-umber/20 tw:text-sm'>
            <tr>
              <td colSpan={3} className='tw:px-4 tw:py-2 tw:text-right tw:text-ed-stone'>Subtotal</td>
              <td className='tw:px-4 tw:py-2 tw:text-right tw:tabular-nums'>{formatJpy(invoice.subtotalGross)}</td>
              <td />
            </tr>
            {invoice.commissionDeductions.map((deduction) => (
              <tr key={deduction.percent}>
                <td colSpan={3} className='tw:px-4 tw:py-2 tw:text-right tw:text-ed-stone'>
                  Commission deduction ({deduction.percent}%)
                </td>
                <td className='tw:px-4 tw:py-2 tw:text-right tw:tabular-nums'>
                  {formatCommissionDeduction(deduction.amount, formatJpy)}
                </td>
                <td />
              </tr>
            ))}
            <tr className='tw:border-t tw:border-ed-umber/20 tw:font-medium'>
              <td colSpan={3} className='tw:px-4 tw:py-3 tw:text-right'>合計（税込）</td>
              <td className='tw:px-4 tw:py-3 tw:text-right tw:text-base tw:tabular-nums'>{formatJpy(invoice.total)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <PortalPanel invoice={invoice} />

      <Dialog open={confirmingIssue} onOpenChange={setConfirmingIssue}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue this invoice?</DialogTitle>
            <DialogDescription asChild>
              <div className='tw:space-y-2 tw:text-sm'>
                <p>
                  {invoice.partnerName} will be billed {formatJpy(invoice.total)} for{' '}
                  {invoicePeriodLabel(invoice.period)}.
                </p>
                <p>
                  This marks all {invoice.lines.length} bookings as invoiced, so they cannot be billed
                  again, and renders the PDF. The figures stop being editable — anything that changes
                  afterwards goes on next month&rsquo;s invoice.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setConfirmingIssue(false)} disabled={busy !== null}>
              Cancel
            </Button>
            <Button disabled={busy !== null} onClick={() => void run('issue', () => issueInvoice({ invoiceId }))}>
              {busy === 'issue' && <Loader2 className='tw:size-4 tw:animate-spin' />}
              Issue invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmingVoid} onOpenChange={setConfirmingVoid}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void this invoice?</DialogTitle>
            <DialogDescription asChild>
              <div className='tw:space-y-2 tw:text-sm'>
                <p>
                  Its {invoice.lines.length} bookings are released and will appear on the next draft.
                </p>
                <p className='tw:font-medium tw:text-ed-black'>
                  Only do this if the partner has not been sent it. If they are already holding it, the
                  money has been asked for — cancel the booking or let the change fall onto next
                  month&rsquo;s invoice as an adjustment instead.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setConfirmingVoid(false)} disabled={busy !== null}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              disabled={busy !== null}
              onClick={() => void run('void', () => voidInvoice({ invoiceId }))}
            >
              {busy === 'void' && <Loader2 className='tw:size-4 tw:animate-spin' />}
              Void invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * What Portal holds about this invoice, and what still has to be done by hand.
 *
 * Shown rather than left implicit because the manual step is the easiest thing
 * in the workflow to forget: the CMS produces the document and stops, and
 * everything commercial — when it went out, whether it was paid — lives over
 * there. Naming Portal's own fields here is what makes the gap legible.
 */
function PortalPanel({ invoice }: { invoice: InvoiceType }) {
  if (invoice.status !== 'issued') return null;

  return (
    <section className='tw:mt-6 tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-ed-cream/30 tw:p-5'>
      <p className='tw:font-medium'>Portal</p>
      <p className='tw:mt-1 tw:text-sm tw:text-ed-stone'>
        Not connected yet. Download the PDF above and upload it in Portal against this partner, then
        fill in <em>Date invoice sent</em>, <em>Type</em>, <em>Amount</em> ({formatJpy(invoice.total)}),{' '}
        <em>Memo</em>, <em>Date due</em> ({invoice.dueDate ? formatCalendarDate(invoice.dueDate) : '—'}) and{' '}
        <em>Payment method</em>. Payment status stays Portal&rsquo;s — it is not mirrored here.
      </p>
    </section>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <p className='tw:mb-4 tw:flex tw:items-start tw:gap-2 tw:rounded-md tw:border tw:border-amber-300 tw:bg-amber-50 tw:px-4 tw:py-3 tw:text-sm'>
      <AlertTriangle className='tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-amber-600' />
      <span>{children}</span>
    </p>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className='tw:text-xs tw:tracking-wider tw:uppercase tw:text-ed-stone'>{label}</p>
      <p className='tw:mt-1'>{children}</p>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to='/admin/invoices'
      className='tw:inline-flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-ed-stone tw:underline-offset-4 tw:hover:underline'
    >
      <ArrowLeft className='tw:size-4' /> All invoices
    </Link>
  );
}
